import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import type { ConsolidatedEventCluster, UserAssertion } from '@/types';
import { formatVideoTime } from '@/types';
import {
  agreementMetricColor,
  clusterHasOfficialAnswer,
  formatClusterAgreementDisplayLabel,
  getClusterAssertionForUser,
  getClusterOfficialAssertions,
  getClusterOwnerAssertion,
  isAssertionAlignedWithOfficials,
  isParticipantAlignedWithOwner,
} from '@/components/review/consensus';
import { ConsensusOpinionCell, eventOnFieldLabel } from '@/components/review/consensusComparisonCells';

interface ParticipantColumn {
  userId: string;
  label: string;
}

interface OfficialColumn {
  userId: string;
  label: string;
}

interface Props {
  clusters: ConsolidatedEventCluster[];
  participantColumns: ParticipantColumn[];
  officialColumns: OfficialColumn[];
  participantView: boolean;
  currentUserId?: number;
  officialUserIds?: number[];
  videoId?: string | null;
  onJumpToTime?: (seconds: number) => void;
  onPlayCluster: (cluster: ConsolidatedEventCluster, onFieldLabel: string) => void;
  canEditAssertion: (assertion: UserAssertion | null) => boolean;
  onEdit: (e: React.MouseEvent<HTMLButtonElement>, assertion: UserAssertion) => void;
}

function findParticipantAssertion(
  cluster: ConsolidatedEventCluster,
  userId: string
): UserAssertion | null {
  return cluster.assertions.find((a) => a.role === 'participant' && a.userId === userId) ?? null;
}

function responsePanelSx(aligned: boolean | null, isOfficial: boolean) {
  if (isOfficial) {
    return {
      bgcolor: 'rgba(139,195,74,0.06)',
      borderColor: 'rgba(139,195,74,0.22)',
    };
  }
  if (aligned == null) return {};
  if (aligned) {
    return { bgcolor: 'rgba(46,125,50,0.07)', borderColor: 'rgba(46,125,50,0.22)' };
  }
  return {
    bgcolor: 'rgba(198,40,40,0.08)',
    borderColor: 'rgba(198,40,40,0.28)',
    boxShadow: (t: { palette: { error: { main: string } } }) =>
      `inset 3px 0 0 ${t.palette.error.main}`,
  };
}

export default function ConsensusComparisonMobileList({
  clusters,
  participantColumns,
  officialColumns,
  participantView,
  currentUserId,
  officialUserIds,
  videoId,
  onJumpToTime,
  onPlayCluster,
  canEditAssertion,
  onEdit,
}: Props) {
  const theme = useTheme();

  return (
    <Stack spacing={0.75}>
      {clusters.map((cluster) => {
        const officialAssertions = getClusterOfficialAssertions(cluster, officialUserIds);
        const ownerAssertion = getClusterOwnerAssertion(cluster, officialUserIds);
        const hasOfficialAnswer = clusterHasOfficialAnswer(cluster, officialUserIds);
        const onFieldLabel = eventOnFieldLabel(cluster, ownerAssertion);
        const consensusRate = cluster.agreementRate;
        const myAligned =
          participantView && currentUserId != null
            ? isParticipantAlignedWithOwner(cluster, currentUserId, officialUserIds)
            : null;
        const rowRate = participantView
          ? myAligned == null
            ? 0
            : myAligned
              ? 100
              : 0
          : consensusRate ?? 0;
        const rowTone = agreementMetricColor(rowRate, theme.palette.mode === 'dark');

        return (
          <Accordion
            key={cluster.clusterId}
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              '&:before': { display: 'none' },
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 48,
                px: 1.25,
                '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center' },
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ width: '100%', pr: 0.5, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  fontFamily="monospace"
                  sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatVideoTime(cluster.anchorTimestamp)}
                </Typography>
                {(videoId || onJumpToTime) && (
                  <Tooltip title={videoId ? '播放影片片段' : '跳到影片'}>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlayCluster(cluster, onFieldLabel);
                      }}
                      sx={{ p: 0.25, flexShrink: 0 }}
                    >
                      <PlayArrowIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap title={onFieldLabel}>
                  {onFieldLabel}
                </Typography>
                <Chip
                  size="small"
                  label={
                    participantView
                      ? myAligned == null
                        ? '未標記'
                        : myAligned
                          ? '一致'
                          : '分歧'
                      : formatClusterAgreementDisplayLabel(cluster, officialUserIds)
                  }
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    bgcolor: rowTone.bg,
                    color: rowTone.color,
                    border: '1px solid',
                    borderColor: rowTone.border,
                  }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
              <Stack spacing={1}>
                {officialColumns.map((col, colIndex) => {
                  const assertion = col.userId
                    ? getClusterAssertionForUser(cluster, col.userId)
                    : ownerAssertion;
                  return (
                    <Box
                      key={col.userId || 'official-default'}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        ...responsePanelSx(null, true),
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
                        {colIndex === 0 &&
                          (hasOfficialAnswer ? (
                            <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                          ) : (
                            <GroupsIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          ))}
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          {col.label}
                        </Typography>
                      </Stack>
                      <ConsensusOpinionCell
                        assertion={assertion}
                        ownerAssertion={assertion}
                        highlightReviewedDiff={false}
                        canEdit={canEditAssertion(assertion)}
                        onEdit={onEdit}
                      />
                    </Box>
                  );
                })}
                {participantColumns.map((col) => {
                  const assertion = findParticipantAssertion(cluster, col.userId);
                  const aligned =
                    assertion && officialAssertions.length
                      ? isAssertionAlignedWithOfficials(assertion, officialAssertions)
                      : null;
                  return (
                    <Box
                      key={col.userId}
                      sx={{
                        p: 1.25,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        ...responsePanelSx(aligned, false),
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {col.label}
                      </Typography>
                      <ConsensusOpinionCell
                        assertion={assertion}
                        ownerAssertions={officialAssertions}
                        highlightReviewedDiff={hasOfficialAnswer}
                        canEdit={canEditAssertion(assertion)}
                        onEdit={onEdit}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
